import { LinkLandingComponent } from './link-landing/link-landing.component';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HeatmapComponent } from './heatmap/heatmap.component';
import { TemperatureComponent } from './temperature/temperature.component';
import { Screen3Component } from './screen3/screen3.component';
import { CuringComponent } from './curing/curing.component';
import { SubviewsComponent } from './subviews/subviews.component';
import { HealthComponent } from './health/health.component';
import { ProjectComponent } from './project/project.component';
import { ProjectInformationComponent } from './project-information/project-information.component';
import { GraphComponent } from './graph/graph.component';
import { LogoutComponent } from './logout/logout.component';
import { LoginComponent } from './login/login.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { MsalGuard } from '@azure/msal-angular';
import { userAuthGuard } from './user-auth.guard';
import { CustomerSelectorComponent } from './customer-selector/customer-selector.component';
import { ProjectSelectionComponent } from './project-selection/project-selection.component';
import { AlertsComponent } from './alerts/alerts.component';
import { CureSheetsComponent } from './cure-sheets/cure-sheets.component';
import { msalRedirectSettledGuard } from './auth/msal-redirect-settled.guard';

const routes: Routes = [
  {
    component: LoginComponent,
    path: 'login',
    // Same hold: an unauthenticated return to '/' is routed here, and that
    // navigation must not run before MSAL has consumed the response either.
    canMatch: [msalRedirectSettledGuard],
  },
  {
    component: UnauthorizedComponent,
    path: 'unauthorized',
  },
  // PUBLIC: invite links. No guard, no session needed to load. The link is read
  // and stripped from the URL there (auth/pending-link.ts). /bind is RETIRED (Joel,
  // 2026-10-02): a customer's directory is bound from the domains typed at Add
  // Customer, so there is no binding link to land on.
  { component: LinkLandingComponent, path: 'invite' },
  {
    component: CustomerSelectorComponent,
    path: 'select-customer',
  },
  {
    component: MainLayoutComponent,
    path: '',
    canActivate: [userAuthGuard], // Add your authentication guard here if needed
    children: [
      {
        // SELECTION IS THE LANDING ROUTE. Every other tab in this tree renders a
        // cure, so arriving anywhere else with nothing selected shows an empty
        // shell that reads as a broken app rather than as a choice not yet made.
        path: '',
        redirectTo: 'projects',
        pathMatch: 'full',
        // HELD UNTIL MSAL HAS READ THE FRAGMENT. This is the app's redirectUri,
        // so it is where Entra returns with `#code=…`, and this redirect was
        // destroying that response before MsalRedirectComponent saw it —
        // measured: hash "" and not one line of MSAL output. canMatch, because a
        // route with redirectTo never runs canActivate.
        canMatch: [msalRedirectSettledGuard]
      },
      {
        component: ProjectSelectionComponent,
        path: 'projects',
      },
      {
        component: ProjectComponent,
        path: 'project',
      },
      {
        component: TemperatureComponent,
        path: 'temperature',
      },
      {
        component: GraphComponent,
        path: 'graph',
      },
      {
        component: CuringComponent,
        path: 'curing',
      },
      {
        component: SubviewsComponent,
        path: 'subviews',
      },
      {
        component: Screen3Component,
        path: 'notes',
      },
      {
        component: HealthComponent,
        path: 'health',
      },
      {
        component: ProjectInformationComponent,
        path: 'info',
      },
      {
        component: LogoutComponent,
        path: 'logout',
      },
      {
        component: AlertsComponent,
        path: 'alerts',
      },
      {
        // Not lazy, unlike /admin: this is a customer-facing nav item rather
        // than an explicit trip into an admin area, and the screen is small.
        component: CureSheetsComponent,
        path: 'cure-sheets',
      },
      {
        path: 'settings/preferences',
        redirectTo: 'admin/preferences',
        pathMatch: 'full',
      },
      {
        // LAZY. Nothing under /admin is on the path to first paint — it is reached
        // only by an explicit click from the user menu. The child routes and their
        // adminGuard now live in AdminModule so none of it ships in main.js.
        path: 'admin',
        loadChildren: () =>
          import('./admin/admin.module').then((m) => m.AdminModule),
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // THE ROUTER MUST NOT MOVE UNTIL MSAL HAS READ THE FRAGMENT.
      //
      // Measured, with a document-start probe on the real dev server:
      //   +0ms   path=/       hash=#code=…&state=…
      //   +23ms  pushState -> path=/login   hash=(empty)
      // The initial navigation ran, the parent route's userAuthGuard saw no
      // account (because MSAL had not processed the response yet), sent the user
      // to /login, and Angular's new UrlTree dropped the fragment. MSAL then
      // found nothing: no error, no rejection, silence.
      //
      // A canMatch guard on the landing route cannot fix this — the PARENT's
      // canActivate runs first — and waiting on inProgress$ cannot either,
      // because it starts at InteractionStatus.None, so "wait for None" is
      // satisfied before MSAL has begun. Ordering has to be explicit:
      // AppComponent calls router.initialNavigation() after
      // handleRedirectPromise resolves. See app.component.ts.
      initialNavigation: 'disabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
